import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'
import type { Tone } from '../lib/labels'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Removes the border and shadow, for a card nested inside another panel. */
  flush?: boolean
}

export function Card({ className, flush, children, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-card bg-surface',
        !flush && 'border border-line shadow-card',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

export interface CardHeaderProps {
  title: ReactNode
  description?: ReactNode
  /** Buttons or filters, right aligned on the same row as the title. */
  actions?: ReactNode
  className?: string
  /** Heading level, so a page keeps one sensible outline. */
  as?: 'h2' | 'h3' | 'h4'
  /**
   * The title's size. `sm` is 13/18, the compact header most cards already
   * use. `md` is the card or modal title, 16/24. `lg` is the section and
   * integration card title, 18/28. All three are semibold in the full ink.
   */
  size?: CardHeaderSize
}

export type CardHeaderSize = 'sm' | 'md' | 'lg'

const CARD_TITLE_SIZE: Record<CardHeaderSize, string> = {
  sm: 'text-sm',
  md: 'text-title',
  lg: 'text-xl',
}

export function CardHeader({ title, description, actions, className, as: Heading = 'h2', size = 'sm' }: CardHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3',
        className,
      )}
    >
      <div className="min-w-0">
        <Heading className={cn(CARD_TITLE_SIZE[size], 'font-semibold text-ink')}>{title}</Heading>
        {description ? (
          <p className={cn('mt-0.5 text-ink-muted', size === 'sm' ? 'text-xs' : 'text-base')}>{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  )
}

export function CardBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('px-4 py-4', className)} {...rest}>
      {children}
    </div>
  )
}

export function CardFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-wrap items-center justify-end gap-2 border-t border-line px-4 py-3', className)}
      {...rest}
    >
      {children}
    </div>
  )
}

/** A labelled block of key and value pairs, the workhorse of every detail page. */
export function DescriptionList({
  items,
  columns = 2,
  className,
}: {
  items: { term: ReactNode; value: ReactNode; span?: boolean }[]
  columns?: 1 | 2 | 3
  className?: string
}) {
  const gridClass =
    columns === 1 ? 'grid-cols-1' : columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-3', gridClass, className)}>
      {items.map((item, index) => (
        <div key={index} className={cn('min-w-0', item.span && 'sm:col-span-full')}>
          <dt className="text-2xs font-medium uppercase tracking-wide text-ink-subtle">{item.term}</dt>
          <dd className="mt-0.5 break-words text-sm text-ink">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * A compact stack of key and value rows, term on the left and value right
 * aligned. Use it in a narrow panel beside the main content. `DescriptionList`
 * is the one for the wide grid of facts at the top of a detail page.
 */
export function DetailList({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cn('divide-y divide-line', className)}>{children}</dl>
}

export function DetailRow({ term, children }: { term: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <dt className="shrink-0 text-2xs uppercase tracking-wide text-ink-subtle">{term}</dt>
      <dd className="min-w-0 text-right text-xs text-ink">{children}</dd>
    </div>
  )
}

/**
 * The page heading block moved to `PageHeader.tsx`, where it grew a tab slot
 * and the band rules that go with it.
 *
 * Re-exported from here rather than simply moved, because a page imports it
 * either from the barrel or straight from this file, and both spellings are in
 * the tree. The header is no longer a card, so it no longer lives with them.
 */
export { PageHeader, SectionHeaders } from './PageHeader'
export type { PageHeaderProps, PageHeaderVariant } from './PageHeader'

/**
 * A short highlighted note. Use sparingly, and never for ordinary content.
 *
 * `live` is for a callout that appears as the result of something the person
 * just did, such as a blocked action: it announces itself instead of waiting to
 * be found.
 */
export function Callout({
  tone = 'info',
  title,
  children,
  icon,
  className,
  actions,
  live = false,
}: {
  tone?: Tone
  title?: ReactNode
  children?: ReactNode
  icon?: ReactNode
  className?: string
  actions?: ReactNode
  live?: boolean
}) {
  const tones: Record<Tone, string> = {
    info: 'border-info/30 bg-info-soft text-info-text',
    warning: 'border-warning/30 bg-warning-soft text-warning-text',
    danger: 'border-danger/30 bg-danger-soft text-danger-text',
    success: 'border-success/30 bg-success-soft text-success-text',
    accent: 'border-accent/30 bg-accent-soft text-accent-text',
    neutral: 'border-line bg-surface-muted text-ink-muted',
  }
  return (
    <div
      role={live ? (tone === 'danger' ? 'alert' : 'status') : undefined}
      aria-live={live ? 'polite' : undefined}
      className={cn('rounded-md border px-3 py-2.5 text-sm', tones[tone], className)}
    >
      <div className="flex items-start gap-2.5">
        {icon ? <span className="mt-0.5 shrink-0">{icon}</span> : null}
        <div className="min-w-0 flex-1">
          {title ? <p className="font-semibold">{title}</p> : null}
          {children ? <div className={cn(title && 'mt-0.5', 'text-[0.8125rem] leading-relaxed')}>{children}</div> : null}
        </div>
        {actions ? <div className="shrink-0">{actions}</div> : null}
      </div>
    </div>
  )
}

/**
 * A big pickable card, used wherever a choice should feel like one click rather
 * than a radio button somebody has to aim at.
 */
export function ChoiceCard({
  selected = false,
  onSelect,
  title,
  meta,
  children,
  className,
  disabled = false,
}: {
  selected?: boolean
  onSelect: () => void
  title: ReactNode
  /** Small right-aligned note on the title row, such as a count. */
  meta?: ReactNode
  children?: ReactNode
  className?: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'flex w-full flex-col gap-2 rounded-card border p-4 text-left transition-all',
        'focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-55',
        selected
          ? 'border-accent bg-accent-soft shadow-raised ring-1 ring-accent'
          : 'border-line bg-surface shadow-card hover:-translate-y-px hover:border-line-strong hover:shadow-raised',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-sm font-semibold text-ink">{title}</span>
        {meta ? <span className="shrink-0 text-2xs text-ink-muted">{meta}</span> : null}
      </div>
      {children}
    </button>
  )
}
